import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  HiChevronDown,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
  HiOutlineClock,
  HiOutlineDocumentText,
  HiOutlineDotsVertical,
  HiOutlineExclamationCircle,
  HiOutlineLockClosed,
  HiOutlineSwitchHorizontal,
  HiOutlineX,
  HiUsers,
} from "react-icons/hi";
import { LuCopy } from "react-icons/lu";
import { useHistory } from "react-router-dom";
import { ADMISSION_STATUS, FORCE_STATUS_TARGETS, STATUS_CONFIG, STATUS_REASON_LABELS, TERMINAL_ADMISSION_STATUSES, getAllowedForceStatusTargets } from "../../utils";

const BOT_LEASE_TIMEOUT_MS = 10 * 60 * 1000;

const FORCE_STATUS_DOT_CLASSES = {
  [ADMISSION_STATUS.TO_VALIDATE]: "bg-orange-500",
  [ADMISSION_STATUS.RECORDED]: "bg-emerald-500",
  [ADMISSION_STATUS.ARCHIVED]: "bg-gray-400",
};

const AdmissionHeader = forwardRef(
  (
    {
      currentAdmissionId,
      filteredIds,
      displayAdmission,
      admission,
      isBackgroundLoading,
      hasValidationErrors,
      nbConcurrentUsers = 0,
      onClose,
      onNavigateToOffset,
      onValidate,
      onSendToProduction,
      onReject,
      onToggleHold,
      onOpenAuditLog,
      onForceStatus,
    },
    ref,
  ) => {
    const history = useHistory();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isDuplicatesOpen, setIsDuplicatesOpen] = useState(false);
    const [isForceSectionOpen, setIsForceSectionOpen] = useState(false);
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);
    const duplicatesRef = useRef(null);
    const currentIndex = filteredIds.length > 0 ? filteredIds.indexOf(currentAdmissionId) : -1;
    const hasNavigation = filteredIds.length > 1 && currentIndex !== -1;
    const birthDate = displayAdmission?.patient?.birthDate ? new Date(displayAdmission.patient.birthDate).toLocaleDateString("fr-FR") : null;
    const isTerminalStatus = TERMINAL_ADMISSION_STATUSES.includes(admission?.status);
    const isBotLocked =
      admission?.status === ADMISSION_STATUS.QUEUED &&
      !!admission?.assignedBot &&
      !!admission?.dispatchedAt &&
      new Date(admission.dispatchedAt).getTime() > Date.now() - BOT_LEASE_TIMEOUT_MS;
    const forceStatusTargets = isBotLocked === true ? [] : getAllowedForceStatusTargets(admission?.status);
    const getForceStatusDisabledReason = (targetStatus) => {
      if (targetStatus === admission?.status) return "Statut actuel";
      if (isBotLocked === true) return "En cours d'envoi au SIL";
      return "Transition non disponible";
    };
    const hasAdmission = Boolean(admission?._id);
    const canAct = hasAdmission === true && isTerminalStatus !== true && isBackgroundLoading !== true;
    const canSubmit = canAct && hasValidationErrors !== true;
    const isIdeSource = displayAdmission?.source === "IDE";
    const typeLabel = isIdeSource ? "IDE" : displayAdmission?.type === "LATER" ? "Domicile" : "Laboratoire";
    const duplicates = displayAdmission?.children || [];
    const parentAdmissionId = displayAdmission?.parentAdmissionId;
    const parentNumber = displayAdmission?.parentNumber;
    const isNotEligible = displayAdmission?.flags?.includes("NOT_ELIGIBLE");
    const isUrgent = (!!displayAdmission?.checkInDate && ![ADMISSION_STATUS.RECORDED, ADMISSION_STATUS.ARCHIVED].includes(displayAdmission?.status)) || isIdeSource;

    useEffect(() => {
      const onClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsDropdownOpen(false);
        if (menuRef.current && !menuRef.current.contains(event.target)) setIsMenuOpen(false);
        if (duplicatesRef.current && !duplicatesRef.current.contains(event.target)) setIsDuplicatesOpen(false);
      };

      document.addEventListener("mousedown", onClickOutside);
      return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    useEffect(() => {
      setIsDropdownOpen(false);
      setIsMenuOpen(false);
      setIsDuplicatesOpen(false);
    }, [currentAdmissionId]);

    useImperativeHandle(
      ref,
      () => ({
        closeUi: () => {
          const hasOpenUi = isDropdownOpen === true || isMenuOpen === true;
          if (hasOpenUi !== true) return false;

          setIsDropdownOpen(false);
          setIsMenuOpen(false);
          return true;
        },
      }),
      [isDropdownOpen, isMenuOpen],
    );

    return (
      <div className="flex h-12 items-center gap-2 border-b border-gray-200 bg-white px-3">
        <button type="button" className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600" onClick={onClose} title="Fermer">
          <HiOutlineX className="h-4 w-4" />
        </button>

        {hasNavigation && (
          <>
            <div className="mx-0.5 h-4 w-px bg-gray-200" />
            <button
              type="button"
              className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
              onClick={() => onNavigateToOffset(-1)}
              disabled={currentIndex <= 0}
              title="Précédent (k)"
            >
              <HiOutlineChevronUp className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
              onClick={() => onNavigateToOffset(1)}
              disabled={currentIndex >= filteredIds.length - 1}
              title="Suivant (j)"
            >
              <HiOutlineChevronDown className="h-4 w-4" />
            </button>
            <span className="ml-1 text-xs tabular-nums text-gray-500">
              {currentIndex + 1} / {filteredIds.length}
            </span>
          </>
        )}

        <div className="mx-2 h-5 w-px bg-gray-200" />
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <h1 className="truncate text-sm font-semibold text-gray-900">
            {displayAdmission?.patient?.firstName} {displayAdmission?.patient?.lastName}
          </h1>
          {birthDate && <span className="shrink-0 text-xs tabular-nums text-gray-500">{birthDate}</span>}
          {displayAdmission?.number && (
            <span className="shrink-0 font-mono text-[11px] uppercase tracking-wide text-gray-400">
              {displayAdmission.number}
              {displayAdmission.silReservationNumber && <span className="ml-1 text-gray-300">· {displayAdmission.silReservationNumber}</span>}
              {displayAdmission.silAdmissionNumber && <span className="ml-1 text-gray-300">· {displayAdmission.silAdmissionNumber}</span>}
            </span>
          )}
          {displayAdmission?.status && (
            <span className="status-pill" data-status={displayAdmission.status} title={isBotLocked === true ? "En cours d'envoi au SIL" : undefined}>
              {STATUS_CONFIG[displayAdmission.status]?.label || displayAdmission.status}
              {[ADMISSION_STATUS.ARCHIVED, ADMISSION_STATUS.RECORDED].includes(displayAdmission.status) &&
                STATUS_REASON_LABELS[displayAdmission.statusReason] &&
                ` · ${STATUS_REASON_LABELS[displayAdmission.statusReason]}`}
              {isBotLocked === true && <HiOutlineLockClosed className="h-3 w-3" />}
            </span>
          )}
          {displayAdmission?.type && <span className="chip border-blue-200 bg-blue-50 text-blue-700">{typeLabel}</span>}
          {isNotEligible && (
            <span className="chip border-red-500 bg-red-500 text-white">
              <HiOutlineExclamationCircle className="h-3 w-3" />
              Non éligible
            </span>
          )}
          {isUrgent && (
            <span className="chip border-red-200 bg-red-50 text-red-700" title="Patient présent — à traiter en priorité">
              <HiOutlineClock className="h-3 w-3" />
              Urgent
            </span>
          )}
          {duplicates.length === 1 && (
            <button
              type="button"
              onClick={() => history.push(`/admissions/${duplicates[0]._id}`)}
              className="chip border-blue-200 bg-blue-50 text-blue-700 transition-colors hover:bg-blue-100"
              title="Ouvrir le dossier dupliqué"
            >
              <LuCopy className="h-3 w-3" />
              Dupliqué : {duplicates[0].number}
            </button>
          )}
          {duplicates.length > 1 && (
            <div className="relative" ref={duplicatesRef}>
              <button
                type="button"
                onClick={() => setIsDuplicatesOpen((prev) => !prev)}
                className="chip border-blue-200 bg-blue-50 text-blue-700 transition-colors hover:bg-blue-100"
              >
                <LuCopy className="h-3 w-3" />
                {duplicates.length} duplications
                <HiChevronDown className={`h-3 w-3 transition-transform ${isDuplicatesOpen ? "rotate-180" : ""}`} />
              </button>
              {isDuplicatesOpen && (
                <div className="dropdown-panel absolute left-0 top-full z-20 mt-1 w-56 overflow-hidden">
                  {duplicates.map((dup) => (
                    <button
                      key={dup._id}
                      type="button"
                      onClick={() => {
                        setIsDuplicatesOpen(false);
                        history.push(`/admissions/${dup._id}`);
                      }}
                      className="flex w-full items-center justify-between px-3 py-2 text-xs text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      <span className="font-medium">{dup.number}</span>
                      <span className="text-gray-400">{new Date(dup.createdAt).toLocaleDateString("fr-FR")}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {parentAdmissionId && (
            <button
              type="button"
              onClick={() => history.push(`/admissions/${parentAdmissionId}`)}
              className="chip border-purple-200 bg-purple-50 text-purple-700 transition-colors hover:bg-purple-100"
              title="Ouvrir le dossier source"
            >
              <LuCopy className="h-3 w-3" />
              Source : {parentNumber}
            </button>
          )}
          {nbConcurrentUsers > 0 && (
            <span className="chip border-yellow-200 bg-yellow-50 text-yellow-800">
              <HiUsers className="h-3 w-3" />
              {nbConcurrentUsers} autre{nbConcurrentUsers > 1 ? "s" : ""}
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600"
              onClick={() => {
                setIsMenuOpen(!isMenuOpen);
                setIsForceSectionOpen(false);
              }}
              title="Plus d'actions"
            >
              <HiOutlineDotsVertical className="h-5 w-5" />
            </button>
            {isMenuOpen && (
              <div className="absolute right-0 z-10 mt-1 w-56 rounded-lg border border-gray-200 bg-white shadow-lg">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-t-lg px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
                  onClick={() => {
                    setIsMenuOpen(false);
                    onReject();
                  }}
                  disabled={hasAdmission !== true || isBackgroundLoading === true || isTerminalStatus === true}
                >
                  <HiOutlineX className="h-4 w-4 text-red-500" />
                  Refuser le dossier
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 border-t border-gray-100 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  onClick={() => setIsForceSectionOpen((prev) => !prev)}
                >
                  <HiOutlineSwitchHorizontal className="h-4 w-4 shrink-0 text-gray-400" />
                  Forcer le statut
                  <HiChevronDown className={`ml-auto h-3 w-3 shrink-0 text-gray-400 transition-transform ${isForceSectionOpen ? "rotate-180" : ""}`} />
                </button>
                {isForceSectionOpen === true &&
                  FORCE_STATUS_TARGETS.map((targetStatus) => {
                    const isAllowed = forceStatusTargets.includes(targetStatus) === true;
                    return (
                      <button
                        key={targetStatus}
                        type="button"
                        className="flex w-full items-center gap-2 py-2 pl-9 pr-3 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
                        disabled={isAllowed !== true || isBackgroundLoading === true}
                        title={isAllowed === true ? undefined : getForceStatusDisabledReason(targetStatus)}
                        onClick={() => {
                          setIsMenuOpen(false);
                          onForceStatus(targetStatus);
                        }}
                      >
                        <span className={`h-2 w-2 shrink-0 rounded-full ${isAllowed === true ? FORCE_STATUS_DOT_CLASSES[targetStatus] : "bg-gray-200"}`} />
                        {STATUS_CONFIG[targetStatus]?.label || targetStatus}
                      </button>
                    );
                  })}
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-b-lg border-t border-gray-100 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  onClick={() => {
                    setIsMenuOpen(false);
                    onOpenAuditLog();
                  }}
                >
                  <HiOutlineDocumentText className="h-4 w-4 text-gray-400" />
                  Historique
                </button>
              </div>
            )}
          </div>

          <div className="relative" ref={dropdownRef}>
            <div className="flex shadow-sm">
              {isUrgent === true ? (
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-md rounded-r-none bg-primary-500 px-4 py-2 text-sm text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:opacity-50"
                  disabled={canSubmit !== true}
                  title={isBackgroundLoading === true ? "Chargement..." : "Valider et envoyer en production (Ctrl/⌘ + Shift + S)"}
                  onClick={onSendToProduction}
                >
                  {isBackgroundLoading === true ? "Chargement..." : "Valider et envoyer"}
                </button>
              ) : (
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-md rounded-r-none bg-primary-500 px-4 py-2 text-sm text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:opacity-50"
                  disabled={canSubmit !== true}
                  title={isBackgroundLoading === true ? "Chargement..." : "Valider (Ctrl/⌘ + S)"}
                  onClick={onValidate}
                >
                  {isBackgroundLoading === true ? "Chargement..." : "Valider"}
                </button>
              )}
              <div className={`h-auto w-px ${canSubmit === true ? "bg-primary-400" : "bg-gray-300"}`} />
              <button
                type="button"
                className="flex items-center rounded-r-md bg-primary-500 px-2 text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:opacity-50"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                disabled={canAct !== true}
                aria-label="Plus d'options"
              >
                <HiChevronDown className={`h-3 w-3 transition-transform duration-200 ease-in-out ${isDropdownOpen ? "rotate-180" : ""}`} />
              </button>
            </div>
            {isDropdownOpen && (
              <div className="animate-fadeIn dropdown-panel absolute right-0 z-10 mt-2 w-64 overflow-hidden">
                <div className="py-1">
                  {isUrgent === true ? (
                    <button
                      type="button"
                      className="group flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                      onClick={() => {
                        setIsDropdownOpen(false);
                        onValidate();
                      }}
                      disabled={canSubmit !== true}
                      title="Valider sans envoyer en production"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">Valider sans envoyer en production</span>
                        <span className="text-xs leading-tight text-gray-500">Le dossier sera validé mais pas envoyé en production</span>
                      </div>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="group flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                      onClick={() => {
                        setIsDropdownOpen(false);
                        onSendToProduction();
                      }}
                      disabled={canSubmit !== true}
                      title="Envoyer directement en production (Ctrl/⌘ + Shift + S)"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">Envoyer directement en production</span>
                        <span className="text-xs leading-tight text-gray-500">Sans attendre de la validation de la présence du patient</span>
                      </div>
                    </button>
                  )}
                  {admission?.status === ADMISSION_STATUS.ON_HOLD ? (
                    <button
                      type="button"
                      className="group flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 transition-colors hover:bg-gray-50"
                      onClick={() => {
                        setIsDropdownOpen(false);
                        onToggleHold();
                      }}
                      title="Remettre le dossier à valider"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">Débloquer le dossier</span>
                        <span className="text-xs leading-tight text-gray-500">Le dossier repassera à valider</span>
                      </div>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="group flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 transition-colors hover:bg-gray-50"
                      onClick={() => {
                        setIsDropdownOpen(false);
                        onToggleHold();
                      }}
                      title="Mettre le dossier en attente"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">Mettre en attente</span>
                        <span className="text-xs leading-tight text-gray-500">Le dossier sera mis de côté en attendant une action</span>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

AdmissionHeader.displayName = "AdmissionHeader";

export default AdmissionHeader;
